
import React from "react";
import { safeFormat } from '@/lib/safeDate';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, CheckCircle, Clock, AlertTriangle, FileText, Calendar, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { format, differenceInDays, parseISO } from "date-fns";

export default function CaseMetrics({ cases, contacts }) {
  // Case Status Breakdown
  const casesByStatus = {
    consultation: cases.filter(c => c.status === 'consultation').length,
    in_progress: cases.filter(c => c.status === 'in_progress').length,
    pending: cases.filter(c => c.status === 'pending').length,
    approved: cases.filter(c => c.status === 'approved').length,
    denied: cases.filter(c => c.status === 'denied').length,
    closed: cases.filter(c => c.status === 'closed').length
  };

  // Case Type Breakdown - USE SALES_ITEM.NAME
  const casesByType = {};
  cases.forEach(c => {
    // Get the case type name from sales_item or fallback to case_type
    const type = c.sales_item?.name || c.case_type || 'Other';
    casesByType[type] = (casesByType[type] || 0) + 1;
  });

  const topCaseTypes = Object.entries(casesByType)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Priority Breakdown
  const urgentCases = cases.filter(c => c.priority === 'urgent').length;
  const highPriorityCases = cases.filter(c => c.priority === 'high').length;

  // Approval Rate
  const totalClosed = casesByStatus.approved + casesByStatus.denied;
  const approvalRate = totalClosed > 0 
    ? ((casesByStatus.approved / totalClosed) * 100).toFixed(1)
    : 0;

  // Average Processing Time (for approved cases)
  const approvedCases = cases.filter(c => c.status === 'approved');
  const avgProcessingDays = approvedCases.length > 0
    ? approvedCases.reduce((sum, caseItem) => {
        // Check if dates exist before parsing
        if (!caseItem.created_date || !caseItem.updated_date) {
          return sum;
        }
        try {
          const created = parseISO(caseItem.created_date);
          const updated = parseISO(caseItem.updated_date);
          return sum + differenceInDays(updated, created);
        } catch (error) {
          console.error('Error parsing dates:', error);
          return sum;
        }
      }, 0) / approvedCases.length
    : 0;

  // Upcoming Hearings (next 30 days)
  const now = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(now.getDate() + 30);

  const upcomingHearings = cases
    .filter(c => c.next_hearing_date)
    .filter(c => {
      const hearingDate = new Date(c.next_hearing_date);
      return hearingDate >= now && hearingDate <= thirtyDaysFromNow;
    })
    .sort((a, b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date))
    .slice(0, 5);

  const formatCaseType = (type) => {
    if (!type) return 'Other';
    // If it's already formatted (from sales_item.name), return as is
    if (type.includes(' ')) return type;
    // Otherwise format it
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-600">Active Cases</p>
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-slate-900">
                {casesByStatus.in_progress + casesByStatus.pending}
              </p>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {casesByStatus.in_progress} in progress, {casesByStatus.pending} pending
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-600">Approval Rate</p>
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-emerald-700">{approvalRate}%</p>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {casesByStatus.approved} approved, {casesByStatus.denied} denied
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-600">Avg Processing Time</p>
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-slate-900">
                {avgProcessingDays.toFixed(0)}
              </p>
              <p className="text-sm text-slate-500">days</p>
            </div>
            <p className="text-xs text-slate-500 mt-2">For approved cases</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-600">Urgent Cases</p>
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-rose-700">{urgentCases}</p>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {highPriorityCases} high priority
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Case Status Breakdown */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Case Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Consulta</span>
                <span className="text-sm font-bold text-slate-900">{casesByStatus.consultation}</span>
              </div>
              <Progress 
                value={cases.length > 0 ? (casesByStatus.consultation / cases.length) * 100 : 0} 
                className="h-2" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Em Andamento</span>
                <span className="text-sm font-bold text-slate-900">{casesByStatus.in_progress}</span>
              </div>
              <Progress 
                value={cases.length > 0 ? (casesByStatus.in_progress / cases.length) * 100 : 0} 
                className="h-2 [&>div]:bg-blue-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Pendente</span>
                <span className="text-sm font-bold text-slate-900">{casesByStatus.pending}</span>
              </div>
              <Progress 
                value={cases.length > 0 ? (casesByStatus.pending / cases.length) * 100 : 0} 
                className="h-2 [&>div]:bg-amber-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Aprovado</span>
                <span className="text-sm font-bold text-emerald-700">{casesByStatus.approved}</span>
              </div>
              <Progress 
                value={cases.length > 0 ? (casesByStatus.approved / cases.length) * 100 : 0} 
                className="h-2 [&>div]:bg-emerald-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Negado</span>
                <span className="text-sm font-bold text-rose-700">{casesByStatus.denied}</span>
              </div>
              <Progress 
                value={cases.length > 0 ? (casesByStatus.denied / cases.length) * 100 : 0} 
                className="h-2 [&>div]:bg-rose-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Fechado</span>
                <span className="text-sm font-bold text-slate-900">{casesByStatus.closed}</span>
              </div>
              <Progress 
                value={cases.length > 0 ? (casesByStatus.closed / cases.length) * 100 : 0} 
                className="h-2 [&>div]:bg-slate-600" 
              />
            </div>
          </CardContent>
        </Card>

        {/* Top Case Types */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Top Case Types
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {topCaseTypes.length > 0 ? (
              topCaseTypes.map(([type, count], index) => (
                <div key={type}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">
                      {formatCaseType(type)}
                    </span>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                  <Progress 
                    value={(count / cases.length) * 100} 
                    className={`h-2 [&>div]:${
                      index === 0 ? 'bg-blue-600' :
                      index === 1 ? 'bg-purple-600' :
                      index === 2 ? 'bg-indigo-600' :
                      index === 3 ? 'bg-violet-600' :
                      'bg-slate-600'
                    }`}
                  />
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">No case data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Hearings */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Upcoming Hearings (Next 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingHearings.length > 0 ? (
            <div className="space-y-3">
              {upcomingHearings.map((caseItem, index) => {
                const contact = contacts.find(c => c.id === caseItem.contact_id);
                const contactName = contact 
                  ? `${contact.first_name} ${contact.last_name}`
                  : 'Unknown Contact';
                const daysUntil = differenceInDays(new Date(caseItem.next_hearing_date), new Date());

                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{contactName}</p>
                      <p className="text-sm text-slate-600">{formatCaseType(caseItem.case_type)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-blue-600">
                        {safeFormat(new Date(caseItem.next_hearing_date), 'MMM d, yyyy')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">No upcoming hearings in the next 30 days</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
